import React, { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Short label for the screen (e.g. "Login", "CCC") */
  scope?: string;
};

type State = {
  error: Error | null;
};

/**
 * Catches fatal render errors so a missing env var or auth init failure
 * does not leave a blank page in production.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[AppErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`, error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const scope = this.props.scope ? ` (${this.props.scope})` : '';
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'Inter, system-ui, sans-serif',
            background: '#f8fafc',
          }}
        >
          <div
            style={{
              maxWidth: 480,
              width: '100%',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 24,
              boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)',
            }}
          >
            <h1 style={{ margin: '0 0 8px', fontSize: 20, color: '#0f172a' }}>
              Não foi possível carregar a aplicação{scope}
            </h1>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              Ocorreu um erro inesperado ao iniciar. Pode tentar recarregar a página ou voltar ao
              login.
            </p>
            <pre
              style={{
                fontSize: 11,
                overflow: 'auto',
                maxHeight: 220,
                padding: 12,
                background: '#f1f5f9',
                borderRadius: 8,
                marginBottom: 16,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {`name: ${this.state.error.name || '(none)'}
message: ${this.state.error.message || '(empty)'}
toString: ${String(this.state.error)}
cause: ${
                this.state.error.cause != null
                  ? String((this.state.error as Error & { cause?: unknown }).cause)
                  : '(none)'
              }
props: ${(this.state.error as Error & { stack?: string }).stack || '(none)'}`
                .split('\n')
                .slice(0, 24)
                .join('\n')}
            </pre>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  padding: '10px 18px',
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Recarregar
              </button>
              <a
                href="/login"
                style={{
                  padding: '10px 18px',
                  background: '#f1f5f9',
                  color: '#334155',
                  borderRadius: 8,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Ir para login
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
